/**
 * The end-of-run debrief — the pedagogical payload, not a high score (research/01, research/03).
 * Shows the graded outcome narrative, the final four-dimension profile, the calls worth revisiting
 * (with model answers + principles), reflection prompts, an optional full transcript, and replay.
 */
import { useState } from "react";
import type { Dimension, Ending, OptionTier, Scenario } from "@patc/content-schema";
import { gameContent } from "../content";
import type { Scores } from "../game/scoring";
import type { TranscriptEntry } from "../game/machine";
import { Meters } from "./Meters";
import { TierBadge } from "./TierBadge";
import { RunTranscript } from "./RunTranscript";
import logoRed from "../assets/brand/logo-borenius-red.svg";

const SCENARIO_BY_ID = new Map<string, Scenario>(gameContent.scenarios.map((s) => [s.id, s]));

export function EndScreen({
  ending,
  dimensions,
  scores,
  transcript,
  onReplay,
}: {
  ending: Ending | null;
  dimensions: Dimension[];
  scores: Scores;
  transcript: TranscriptEntry[];
  onReplay: () => void;
}) {
  const [showTranscript, setShowTranscript] = useState(false);
  const revisit = transcript.filter(
    (t) => (t.kind === "choice" || t.kind === "knowledge_check") && t.tier && t.tier !== "best",
  );

  return (
    <main className="mx-auto max-w-3xl px-5 py-12 sm:px-6">
      <img src={logoRed} alt="Borenius" className="mb-8 h-6 w-auto" />
      <p className="t-overline text-brand">Debrief</p>
      <span className="b-rule my-4" />
      <h1 className="font-display text-fluid-2xl font-semibold leading-tight text-ink">{ending?.title ?? "Outcome"}</h1>
      {ending && <p className="reading-measure mt-4 text-fluid-lg leading-relaxed text-ink-700">{ending.body}</p>}

      <section className="mt-9">
        <h2 className="t-overline mb-3 text-ink-500">Your final profile</h2>
        <div className="rounded-sm border border-ink-150 bg-card p-5">
          <Meters dimensions={dimensions} scores={scores} />
        </div>
      </section>

      {revisit.length > 0 && (
        <section className="mt-9">
          <h2 className="t-overline mb-3 text-ink-500">What you'd revisit</h2>
          <ul className="flex flex-col gap-4">
            {revisit.map((t, i) => {
              const sc = SCENARIO_BY_ID.get(t.scenarioId);
              if (!sc || sc.kind === "popup") return null;
              const best = sc.choices.find((c) => c.tier === "best") ?? sc.choices[0];
              return (
                <li key={`${t.scenarioId}-${i}`} className="rounded-sm border border-ink-150 bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-medium text-ink">{sc.title}</h3>
                    {t.tier && <TierBadge tier={t.tier as OptionTier} />}
                  </div>
                  {best && (
                    <p className="mt-2 text-sm leading-relaxed text-ink-700">
                      <span className="font-semibold">Model answer. </span>
                      {best.text}
                    </p>
                  )}
                  {best?.principle && (
                    <p className="mt-2 text-sm leading-relaxed text-ink-700">
                      <span className="font-semibold">Principle. </span>
                      {best.principle}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="mt-9">
        <h2 className="t-overline mb-3 text-ink-500">Reflect</h2>
        <ul className="reading-measure flex list-disc flex-col gap-2 pl-5 text-fluid-base leading-relaxed text-ink-700">
          <li>Which issue most threatened closing on time, and why?</li>
          <li>Where did consulting the Bank before acting change the outcome?</li>
          <li>What would you verify earlier next time to avoid a closing-day scramble?</li>
        </ul>
      </section>

      <section className="mt-9">
        <h2 className="t-overline mb-3 text-ink-500">Full transcript</h2>
        <p className="reading-measure mb-4 text-sm text-ink-600">
          Every answer in play order, with the points it earned. Open any one for the model answer and its
          transferable principle.
        </p>
        <button
          type="button"
          onClick={() => setShowTranscript((v) => !v)}
          className="text-sm font-medium text-ink underline-offset-4 hover:underline"
        >
          {showTranscript ? "Hide" : "Show"} all {transcript.length} answers
        </button>
        {showTranscript && (
          <div className="mt-4">
            <RunTranscript transcript={transcript} scores={scores} showProfile={false} />
          </div>
        )}
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onReplay}
          className="inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3 font-medium text-white hover:bg-brand-hover"
        >
          Play again <span aria-hidden="true">→</span>
        </button>
      </div>
    </main>
  );
}
